import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Check, Clock, BookOpen, Video, FileText, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import MarkdownContent from '../components/ui/MarkdownContent';

// Helper to extract video ID and get embed URL
const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  // Direct video URL
  if (url.match(/\.(mp4|webm|ogg)$/i)) {
    return url;
  }
  
  return url;
};

const CourseContent = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [courseRes, enrollmentRes] = await Promise.all([
          api.get(`/courses/${courseId}`),
          api.get(`/enrollments/${courseId}`)
        ]);
        
        if (!enrollmentRes.data.enrolled) {
          navigate(`/formaciones/${courseId}`);
          return;
        }
        
        setCourse(courseRes.data);
        setEnrollment(enrollmentRes.data.enrollment);
      } catch (error) {
        console.error('Error fetching data:', error);
        navigate('/mi-camino');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId, navigate]);

  // Render content based on module type
  const renderModuleContent = (module) => {
    if (!module) return null;
    
    const { content_type, content_url, content_text } = module;
    
    // Video content
    if (content_type === 'video' && content_url) {
      const embedUrl = getVideoEmbedUrl(content_url);
      const isDirectVideo = content_url.match(/\.(mp4|webm|ogg)$/i);
      
      return (
        <div className="card-soft overflow-hidden p-0">
          <div className="aspect-video bg-black">
            {isDirectVideo ? (
              <video 
                controls 
                className="w-full h-full"
                src={embedUrl}
              >
                Tu navegador no soporta el elemento de video.
              </video>
            ) : (
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={module.title}
              />
            )}
          </div>
        </div>
      );
    }
    
    // Audio content
    if (content_type === 'audio' && content_url) {
      return (
        <div className="card-soft p-8">
          <div className="flex items-center gap-6 mb-6">
            <div className="w-20 h-20 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center">
              <Headphones className="w-10 h-10 text-[hsl(var(--accent))]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[hsl(var(--text-secondary))] mb-1">Audio del módulo</p>
              <p className="text-lg font-heading text-[hsl(var(--text-primary))]">{module.title}</p>
            </div>
          </div>
          <audio controls className="w-full" src={content_url}>
            Tu navegador no soporta el elemento de audio.
          </audio>
        </div>
      );
    }
    
    // Text content
    if (content_type === 'text' && content_text) {
      return (
        <div className="card-soft">
          <div className="prose prose-lg max-w-none text-[hsl(var(--text-secondary))]">
            <MarkdownContent content={content_text} />
          </div>
        </div>
      );
    }
    
    // No content yet - placeholder
    return (
      <div className="card-soft bg-[hsl(var(--sage-light))] p-12 text-center">
        <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mx-auto mb-6">
          {content_type === 'video' && <Video className="w-10 h-10 text-[hsl(var(--accent))]" strokeWidth={1.5} />}
          {content_type === 'audio' && <Headphones className="w-10 h-10 text-[hsl(var(--accent))]" strokeWidth={1.5} />}
          {content_type === 'text' && <FileText className="w-10 h-10 text-[hsl(var(--accent))]" strokeWidth={1.5} />}
          {!content_type && <Play className="w-10 h-10 text-[hsl(var(--accent))]" strokeWidth={1.5} />}
        </div>
        <p className="text-[hsl(var(--text-secondary))] mb-2">
          Contenido en preparación
        </p>
        <p className="text-sm text-[hsl(var(--text-tertiary))]">
          El contenido de este módulo estará disponible pronto.
        </p>
      </div>
    );
  };

  const handleModuleComplete = async (moduleId) => {
    try {
      const completedCount = (enrollment?.completed_modules?.length || 0) + 1;
      const totalModules = course?.modules?.length || 1;
      const newProgress = Math.round((completedCount / totalModules) * 100);
      
      await api.put(`/enrollments/${courseId}/progress?progress=${newProgress}&module_id=${moduleId}`);
      
      setEnrollment(prev => ({
        ...prev,
        progress: newProgress,
        completed_modules: [...(prev?.completed_modules || []), moduleId]
      }));
      
      toast.success('¡Módulo completado!');
    } catch (error) {
      toast.error('Error al actualizar el progreso');
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video':
        return Video;
      case 'audio':
        return Headphones;
      case 'text':
        return FileText;
      default:
        return BookOpen;
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[hsl(var(--text-secondary))]">Cargando curso...</p>
        </div>
      </div>
    );
  }

  if (!course || !enrollment) {
    return null;
  }

  const isModuleCompleted = (moduleId) => {
    return enrollment?.completed_modules?.includes(moduleId);
  };

  return (
    <div className="pt-20 min-h-screen bg-white">
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <aside className="lg:w-80 lg:min-h-screen bg-[hsl(var(--sage-light))] lg:fixed lg:left-0 lg:top-20 lg:bottom-0 overflow-y-auto">
          <div className="p-6">
            <Link 
              to="/mi-camino" 
              className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-6 transition-colors text-sm"
              data-testid="course-content-back"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              Mis cursos
            </Link>

            <h2 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-2">
              {course.title}
            </h2>
            
            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-[hsl(var(--text-tertiary))]">Progreso</span>
                <span className="text-[hsl(var(--sage))] font-medium">{enrollment.progress}%</span>
              </div>
              <Progress value={enrollment.progress} className="h-2" />
            </div>

            {/* Modules list */}
            <div className="space-y-2">
              {course.modules?.map((module, index) => {
                const Icon = getContentIcon(module.content_type);
                const completed = isModuleCompleted(module.id);
                
                return (
                  <button
                    key={module.id || index}
                    onClick={() => setActiveModule(index)}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      activeModule === index
                        ? 'bg-white shadow-soft'
                        : 'hover:bg-white/50'
                    }`}
                    data-testid={`module-nav-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        completed
                          ? 'bg-[hsl(var(--sage))] text-white'
                          : activeModule === index
                          ? 'bg-[hsl(var(--accent))] text-white'
                          : 'bg-white text-[hsl(var(--text-tertiary))]'
                      }`}>
                        {completed ? (
                          <Check className="w-4 h-4" strokeWidth={2} />
                        ) : (
                          <span className="text-sm font-medium">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          activeModule === index
                            ? 'text-[hsl(var(--text-primary))]'
                            : 'text-[hsl(var(--text-secondary))]'
                        }`}>
                          {module.title}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-[hsl(var(--text-tertiary))] mt-1">
                          <Icon className="w-3 h-3" strokeWidth={1.5} />
                          <span>{module.duration}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-80">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
            {course.modules?.length > 0 ? (
              <>
                {/* Module header */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-sm text-[hsl(var(--sage))] font-medium">
                      Módulo {activeModule + 1} de {course.modules.length}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-[hsl(var(--text-tertiary))]">
                      <Clock className="w-4 h-4" strokeWidth={1.5} />
                      {course.modules[activeModule]?.duration}
                    </span>
                  </div>
                  <h1 
                    className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-4"
                    data-testid="active-module-title"
                  >
                    {course.modules[activeModule]?.title}
                  </h1>
                  <p className="text-[hsl(var(--text-secondary))]">
                    {course.modules[activeModule]?.description}
                  </p>
                </div>

                {/* Module Content */}
                <div className="mb-8">
                  {renderModuleContent(course.modules[activeModule])}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between gap-4">
                  <Button
                    onClick={() => setActiveModule(Math.max(0, activeModule - 1))}
                    variant="outline"
                    disabled={activeModule === 0}
                    className="btn-outline"
                  >
                    Anterior
                  </Button>

                  <div className="flex gap-3">
                    {!isModuleCompleted(course.modules[activeModule]?.id) && (
                      <Button
                        onClick={() => handleModuleComplete(course.modules[activeModule]?.id)}
                        className="btn-sage"
                        data-testid="mark-complete"
                      >
                        <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Marcar como completado
                      </Button>
                    )}

                    <Button
                      onClick={() => setActiveModule(Math.min(course.modules.length - 1, activeModule + 1))}
                      disabled={activeModule === course.modules.length - 1}
                      className="btn-gold"
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 text-[hsl(var(--sage))] mx-auto mb-6" strokeWidth={1.5} />
                <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-4">
                  Contenido en preparación
                </h2>
                <p className="text-[hsl(var(--text-secondary))]">
                  El contenido de este curso se está preparando. Pronto estará disponible.
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CourseContent;
